import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  name: string | null;
  picture?: string | null;
  role: 'admin' | 'staff';
  isLocalAdmin: boolean;
  lastLogin: string | null;
  allowedMenus: string[];
  editableMenus: string[];
  deletableMenus: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLocalAdmin: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  loginLocal: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasMenuAccess: (menuKey: string) => boolean;
  canEdit: (menuKey: string) => boolean;
  canDelete: (menuKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_URL || '/api';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data.user);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/login`, {
        withCredentials: true,
      });

      if (response.data.authUrl) {
        // Redirect to OIDC provider
        window.location.href = response.data.authUrl;
      } else if (!response.data.oidcEnabled) {
        // OIDC not configured, throw error to show local login
        throw new Error('OIDC not configured');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const loginLocal = useCallback(async (username: string, password: string) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/local`,
        { username, password },
        { withCredentials: true }
      );

      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors - we still want to clear local state
    } finally {
      setUser(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchUser();
  }, [fetchUser]);

  const hasMenuAccess = useCallback((menuKey: string): boolean => {
    if (!user) return false;
    // Local admin has access to everything
    if (user.isLocalAdmin) return true;
    // Check if user has access to this menu
    return user.allowedMenus?.includes(menuKey) ?? false;
  }, [user]);

  const canEdit = useCallback((menuKey: string): boolean => {
    if (!user) return false;
    if (user.isLocalAdmin) return true;
    return user.editableMenus?.includes(menuKey) ?? false;
  }, [user]);

  const canDelete = useCallback((menuKey: string): boolean => {
    if (!user) return false;
    if (user.isLocalAdmin) return true;
    return user.deletableMenus?.includes(menuKey) ?? false;
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isLocalAdmin: user?.isLocalAdmin || false,
    isLoading,
    login,
    loginLocal,
    logout,
    refresh,
    hasMenuAccess,
    canEdit,
    canDelete,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;

